/*
 * NOTE
 * The shelter picks its date format; this file is the single funnel that turns that
 * choice into what the user sees. Three rules hold everywhere below:
 *
 * 1. DISPLAY follows the shelter format — `getDefaultFormat()` reads the active
 *    format from `@Constants` (a plain module, so this helper stays store-free).
 * 2. PARSING never trusts a bare `moment(string)`. Every string parse goes through
 *    `parseDate()`, which tries a strict list: the ACTIVE format first (so an
 *    ambiguous `03/04/2026` resolves the shelter's way), then the frozen legacy
 *    shapes the API still emits, then ISO.
 * 3. THE WIRE is always timezone-naive ISO — `getPayloadDateString` /
 *    `getPayloadDateTimeString`. Never `.toISOString()` (that bakes in the browser
 *    timezone) and never `new Date('2026-07-31')` (that is UTC midnight, i.e. the
 *    previous day in the Americas).
 * */

import moment from 'moment-timezone';
import { useI18n } from 'vue-i18n';
import type { AfDate } from '@Types';
import {
  getActiveMomentDateFormat,
  getActiveShelterTimezone,
  LEGACY_MOMENT_DATE_FORMATS,
  PAYLOAD_DATE_FORMAT,
  PAYLOAD_DATETIME_FORMAT,
  PHP_DATE_FORMATS,
  PHP_TO_MOMENT_DATE_FORMAT,
} from '@Constants';

const DEFAULT_TIME_FORMAT = 'hh:mm A';

// A date string that carries its own zone — trailing `Z` or a `+hh:mm` / `-hhmm` offset.
const ABSOLUTE_ISO_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/;
const DEFAULT_TIME_FORMAT_WITH_SECONDS = 'hh:mm:ss A';

// Time-of-day shapes appended to every date format in the parse list. 12-hour first
// (what the API emits), then the 24-hour forms the meridian-stripping below leaves behind.
const PARSE_TIME_FORMATS = [DEFAULT_TIME_FORMAT, DEFAULT_TIME_FORMAT_WITH_SECONDS, 'HH:mm:ss', 'HH:mm'];

function getParseFormats(): (string | moment.MomentBuiltinFormat)[] {
  const DATE_FORMATS = [getActiveMomentDateFormat(), ...LEGACY_MOMENT_DATE_FORMATS];

  const DATETIME_FORMATS = DATE_FORMATS.flatMap((dateFormat) =>
    PARSE_TIME_FORMATS.map((timeFormat) => `${dateFormat} ${timeFormat}`)
  );

  return [moment.ISO_8601, ...DATETIME_FORMATS, ...DATE_FORMATS];
}

/**
 * The only sanctioned way to turn an unknown date value into a moment.
 * Strings go through the strict format list; everything else (Date, number, moment)
 * is handed to moment untouched.
 */
function parseDate(date: any): moment.Moment {
  const isString = typeof date === 'string';
  if (!isString) return moment(date);

  const strictParsed = moment(date, getParseFormats(), true);
  if (strictParsed.isValid()) return strictParsed;

  // NOTE :: Last resort for shapes no listed format covers — mainly `Date.prototype.toString()`
  // output. The strict list ran first, so an ambiguous DD/MM vs MM/DD string never reaches here.
  const originalSuppress = moment.suppressDeprecationWarnings;
  moment.suppressDeprecationWarnings = true;

  const looseParsed = moment(date);
  moment.suppressDeprecationWarnings = originalSuppress;

  return looseParsed;
}

/** Tolerant parse to a native `Date`, or `null` when nothing matches. */
function getParsedDate(date: any): Date | null {
  const parsed = parseDate(date);

  return parsed.isValid() ? parsed.toDate() : null;
}

function dateRelation(inputDateString?: string) {
  if (!inputDateString) return;

  const { t } = useI18n();

  // NOTE :: Was a positional `split('-')` that assumed ISO order — it read `31-07-2026`
  // as year 31. The tolerant parse resolves the string by the shelter's own format.
  const inputDate = getParsedDate(inputDateString);
  if (!inputDate) throw new Error('Invalid date format');

  const today = new Date();

  let years = today.getFullYear() - inputDate.getFullYear();
  let months = today.getMonth() - inputDate.getMonth();
  let days = today.getDate() - inputDate.getDate();

  if (months < 0 || (months === 0 && days < 0)) {
    years--;
    months += 12;
  }

  if (days < 0) {
    months--;
    const lastMonthDate = new Date(today.getFullYear(), today.getMonth(), 0);
    days += lastMonthDate.getDate();
  }

  if (
    today.getFullYear() === inputDate.getFullYear() &&
    today.getMonth() === inputDate.getMonth() &&
    today.getDate() === inputDate.getDate()
  ) {
    return t('common.today');
  }

  return t('components.dataTable.dateRange', { years, months, days });
}

function formatNumberToDate(
  dateNumber: string,
  includeTime: boolean = false,
  afDateTimeFormat: boolean = false,
  usDateFormat: boolean = false // New parameter
) {
  const timestamp = Number(dateNumber);
  if (isNaN(timestamp)) return;

  const date = new Date(timestamp);

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();

  const formattedMonth = month < 10 ? `0${month}` : month;
  const formattedDay = day < 10 ? `0${day}` : day;

  let formattedDate = `${year}-${formattedMonth}-${formattedDay}`; // Default format

  if (usDateFormat) {
    formattedDate = `${formattedMonth}-${formattedDay}-${year}`; // New MM/DD/YYYY format
  }

  if (includeTime) {
    let hours = date.getHours();
    const minutes = date.getMinutes();

    const amOrPm = hours >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12

    const formattedHours = hours < 10 ? `0${hours}` : hours;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    formattedDate += ` ${formattedHours}:${formattedMinutes}`;

    if (afDateTimeFormat) {
      formattedDate += ` ${amOrPm}`;
    }
  }

  return formattedDate;
}

/**
 * Display a stored date-time — notes, activities, statements, history log, transports.
 *
 * NOTE :: This used to render with `toLocaleDateString`/`toLocaleTimeString`, which meant long
 * form ("August 31, 2026") in the BROWSER's timezone. Two bugs in one: it ignored the shelter's
 * chosen date format, and for an absolute instant it showed a remote staffer their own clock
 * rather than the shelter's.
 *
 * A NAIVE value is already the shelter's wall clock (nine models expose their dates through
 * `formatAsNaiveShelterDateTime`), so it is formatted as-is — converting it would shift it. Only a
 * value carrying a zone is a real instant, and only that gets moved into the shelter's timezone.
 */
function formatDateTime(dateTimeString: string) {
  const parsed = parseDate(dateTimeString);
  if (!parsed.isValid()) return '';

  const timezone = getActiveShelterTimezone();
  const carriesZone = typeof dateTimeString === 'string' && ABSOLUTE_ISO_PATTERN.test(dateTimeString.trim());
  const shouldConvert = !!timezone && carriesZone;

  const displayed = shouldConvert ? parsed.clone().tz(timezone as string) : parsed;

  return displayed.format(`${getActiveMomentDateFormat()} ${DEFAULT_TIME_FORMAT}`);
}

function formatTimestampToDateTime(timestamp: number | string): string {
  const timestampInSeconds = Number(timestamp) / 1000;

  return moment.unix(timestampInSeconds).format('YYYY-MM-DDTHH:mm:ss.SSSSSSZ');
}

function formatStringToRelation(dateString: string) {
  const { t } = useI18n();

  const currentDate: Date = new Date();
  const submissionDate: Date = getParsedDate(dateString) ?? new Date(NaN);

  const millisecondsPerDay: number = 24 * 60 * 60 * 1000;
  const millisecondsPerMonth: number = 30 * millisecondsPerDay;
  const millisecondsPerYear: number = 365 * millisecondsPerDay;

  const timeDifference: number = currentDate.getTime() - submissionDate.getTime();
  const daysDifference: number = Math.round(timeDifference / millisecondsPerDay);
  const monthsDifference: number = Math.round(timeDifference / millisecondsPerMonth);
  const yearsDifference: number = Math.round(timeDifference / millisecondsPerYear);

  if (daysDifference === 0) return t('common.today');
  if (daysDifference < 30) return `${daysDifference} ${t('common.day', daysDifference)} ${t('common.ago')}`;
  if (monthsDifference < 12) return `${monthsDifference} ${t('common.month', monthsDifference)} ${t('common.ago')}`;

  return `${yearsDifference} ${t('common.year', yearsDifference)} ${t('common.ago')}`;
}

function formatTimestampToDate(timestamp: string) {
  const timestampNumber = Number(timestamp);

  if (isNaN(timestampNumber)) {
    return;
  }

  const date = new Date(timestampNumber);

  const year = date.getUTCFullYear();
  const month = ('0' + (date.getUTCMonth() + 1)).slice(-2);
  const day = ('0' + date.getUTCDate()).slice(-2);
  const hours = ('0' + date.getUTCHours()).slice(-2);
  const minutes = ('0' + date.getUTCMinutes()).slice(-2);
  const seconds = ('0' + date.getUTCSeconds()).slice(-2);
  const milliseconds = ('00' + date.getUTCMilliseconds()).slice(-3);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
}

function toIsoString(date: string | Date, defaultFormat: boolean = false) {
  const currentTime = moment();
  const inputDate = parseDate(date);

  const combinedDate = moment.utc({
    year: inputDate.year(),
    month: inputDate.month(),
    day: inputDate.date(),
    hour: currentTime.utc().hour(),
    minute: currentTime.utc().minute(),
    second: currentTime.utc().second(),
    millisecond: currentTime.utc().millisecond(),
  });

  return defaultFormat ? combinedDate.toISOString() : combinedDate.toISOString().split('T')[0];
}

/**
 * Display string for a date, in the shelter's format.
 *
 * NOTE :: This is a DISPLAY producer. For a value that goes to the API use
 * `getPayloadDateString` — the wire is always ISO, never the shelter format.
 */
function formatDateToString(dateInput: any) {
  const parsed = parseDateInput(dateInput);

  return parsed.isValid() ? parsed.format(getDefaultFormat()) : '';
}

/**
 * Wire value for a date-only field: timezone-naive ISO `YYYY-MM-DD`, built from the
 * LOCAL calendar day. The API anchors it to the shelter timezone.
 */
function getPayloadDateString(dateInput: any) {
  const parsed = parseDateInput(dateInput);

  return parsed.isValid() ? parsed.format(PAYLOAD_DATE_FORMAT) : '';
}

/**
 * Wire value for a date+time field: timezone-naive ISO `YYYY-MM-DDTHH:mm:ss`, i.e. the
 * wall clock the user typed. Never `.toISOString()` — that reframes it by the browser
 * timezone and shifts the stored instant for cross-timezone staff (Model A).
 */
function getPayloadDateTimeString(dateInput: any) {
  const parsed = parseDateInput(dateInput);

  return parsed.isValid() ? parsed.format(PAYLOAD_DATETIME_FORMAT) : '';
}

/**
 * Wire value for a "when did this happen" field fed by a DATE-ONLY picker.
 *
 * Sends the time the user actually picked, or the DAY ALONE when they picked none — the API
 * fills the time in from the shelter's clock (`TimezoneHelper::resolveEventDate`).
 *
 * NOTE :: This used to stamp `moment()` here. That is the BROWSER's clock, and the wire value
 * is timezone-naive, so the API anchored a remote staffer's wall clock to the shelter: a São
 * Paulo staffer on a Denver shelter recorded every outcome three hours into that shelter's
 * future, and near midnight onto the wrong business day. The browser cannot know the shelter's
 * zone, so it must not choose the time — it says which DAY, and the server says when.
 */
function getPayloadEventDateString(dateInput: any) {
  const parsed = parseDateInput(dateInput);

  if (!parsed.isValid()) return '';

  const hasNoTime = parsed.format('HH:mm:ss') === '00:00:00';

  return hasNoTime ? parsed.format(PAYLOAD_DATE_FORMAT) : parsed.format(PAYLOAD_DATETIME_FORMAT);
}

/**
 * NOTE :: Returns an INVALID moment for anything unusable — it must never throw.
 *
 * Its four callers all sit inside payload builders that have no try/catch, and most run before
 * the `.then/.catch` chain of the request they feed. A throw there kills the whole save with no
 * toast and no request, so the user sees nothing happen at all. Every caller already branches on
 * `.isValid()`, which is what lets them keep their documented `''` return.
 */
function parseDateInput(dateInput: any): moment.Moment {
  if (isAfDate(dateInput)) return moment(parseInt(dateInput.$date.$numberLong, 10));

  const isNumericTimestamp = typeof dateInput === 'number' && Number.isFinite(dateInput);
  if (isNumericTimestamp) return moment(dateInput);

  const isSupportedInput = typeof dateInput === 'string' || dateInput instanceof Date;
  if (!isSupportedInput) return moment(NaN);

  // NOTE :: `parseDate` on the string, never `new Date(dateInput)` — a date-only ISO string
  // is UTC midnight there, which is the previous day in any negative-offset timezone.
  return parseDate(dateInput);
}

function numberToDate(date: number | string): string {
  const dateNumber = Number(date);

  return new Date(Date.now() + dateNumber * 24 * 60 * 60 * 1000).toISOString();
}

function formatTimestampToSimpleDate(date: number | string, format?: string) {
  const dateFormatted = formatTimestampToDateTime(date);
  const defaultFormat = format || getDefaultFormat();

  return moment(dateFormatted, moment.ISO_8601, true).format(defaultFormat);
}

function getWeightDateTimestamp(weight: any): number {
  if (!weight?.date || typeof weight.date !== 'string') return 0;

  // NOTE :: Shared list FIRST (active format wins on an ambiguous string); the unpadded US
  // shapes trail it because `M/D/YYYY` also matches a padded `03/04/2026` and would steal it.
  const WEIGHT_DATE_FORMATS = [...getParseFormats(), 'M/D/YYYY h:mm A', 'M/D/YYYY'];

  const parsed = moment(weight.date, WEIGHT_DATE_FORMATS, true);
  if (parsed.isValid()) return parsed.valueOf();

  const looseParsed = parseDate(weight.date);
  return looseParsed.isValid() ? looseParsed.valueOf() : 0;
}

function hasValidWeightDate(weight: any): boolean {
  return !!(weight?.weight && weight?.date && typeof weight.date === 'string');
}

// NOTE :: The `$date` guard has to check the INNER value is an object too. `{ $date: null }` is a
// real shape Mongo's relaxed extended JSON emits, and `'$numberLong' in null` is a TypeError — which
// would throw straight out of parseDateInput, past every caller's isValid() branch.
function isAfDate(date: any): date is AfDate {
  const hasDateKey = !!date && typeof date === 'object' && '$date' in date;

  return hasDateKey && !!date.$date && typeof date.$date === 'object' && '$numberLong' in date.$date;
}

function isValidDate(date: any): date is Date {
  return moment.isDate(date) || parseDate(date).isValid();
}

function getDefaultDateFormat(
  date: Date | AfDate | string | number | null | undefined,
  withTime?: boolean,
  timeOnly?: boolean
) {
  const isString = typeof date === 'string';

  const isEmptyString = isString && !date;
  if (isEmptyString) return;

  const format = getDefaultFormat(withTime);
  const today = moment().format(getDefaultFormat());

  if (isAfDate(date)) return formatTimestampToSimpleDate(date.$date.$numberLong, format);

  const isTimeOnly = isString && timeOnly;

  if (isTimeOnly) {
    let str = (date as string).trim();
    const hasMeridian = /AM|PM/i.test(str);

    str = str.replace(/\s?(AM|PM)/i, '');

    const [h, m, s = '00'] = str.split(':');
    const hour = Number(h);

    const meridian = hasMeridian
      ? (date as string).toUpperCase().includes('PM')
        ? 'PM'
        : 'AM'
      : hour >= 12
        ? 'PM'
        : 'AM';

    const hour24 = meridian === 'PM' && hour < 12 ? hour + 12 : meridian === 'AM' && hour === 12 ? 0 : hour;

    date = `${today} ${hour24.toString().padStart(2, '0')}:${m}:${(s || '00').padStart(2, '0')}`;
  }
  if (isString && !isTimeOnly) date = (date as string).replace(/(\d{2}:\d{2}:\d{2})\s?(AM|PM)/i, '$1');

  const isTimestamp = isString && /^-?\d+$/.test(date as string);
  if (isTimestamp) date = Number(date);

  /*
   * NOTE :: The old "format repair" heuristics used to live here — they swapped the parts of a
   * `YYYY-MM-DD` string when the month "looked wrong" and rewrote every `MM-DD-YYYY` string
   * assuming US order. They are deliberately gone. They mangled `31-07-2026` into `2026-31-07`
   * and silently rewrote `03-04-2026` to the US reading. `parseDate`'s strict format list
   * replaces them, and it must NOT be made format-conditional: the same stored string has to
   * mean the same day for every shelter.
   * */

  const parsed = parseDate(date);

  if (!parsed.isValid()) {
    console.error('Invalid date on getDefaultDateFormat()', date);
    if (date == null) return;

    return String(date);
  }

  const outputFormat = timeOnly ? `${getDefaultFormat()} ${DEFAULT_TIME_FORMAT_WITH_SECONDS}` : format;
  return parsed.format(outputFormat);
}

function getPayloadDate(
  date: string,
  output: 'string' | 'utc' | 'iso' = 'utc',
  withTime?: boolean,
  customFormat?: string
) {
  const format = customFormat ?? getDefaultFormat(withTime);

  //prettier-ignore
  switch (output) {
    case 'string': return parseDate(date).format(format);
    case 'utc': return parseDate(date).utc().format(format);
    case 'iso': return toIsoString(date, true);
    default: return parseDate(date).utc().format(format);
  }
}

function getDefaultFormat(withTime: boolean = false) {
  const dateFormat = getActiveMomentDateFormat();

  return withTime ? `${dateFormat} ${DEFAULT_TIME_FORMAT}` : dateFormat;
}

function getDateFromOId(OId: string) {
  const validOId = OId.length === 24;

  return validOId
    ? moment(new Date(parseInt(OId.substring(0, 8), 16) * 1000)).format(getActiveMomentDateFormat())
    : '- ';
}

function timestampToTime(dateString: string): string {
  const isTimeFormat = /^\d{1,2}:\d{2}:\d{2} [APM]{2}$/.test(dateString);
  if (isTimeFormat) return dateString;

  const date = getParsedDate(dateString) ?? new Date(NaN);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  const period = hours >= 12 ? 'PM' : 'AM';

  const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
  return `${formattedHours}:${minutes}:${seconds} ${period}`;
}

function getTimeFromDate(date: Date) {
  return parseDate(date).format(DEFAULT_TIME_FORMAT);
}

function handleInvalidTime(timeString: string): Date {
  const isValidTime = (str: string) => {
    const regex = /^(\d{1,2}):(\d{2}):(\d{2}) (AM|PM)$/;
    return regex.test(str);
  };

  if (!isValidTime(timeString)) {
    return new Date();
  }

  const [, hours, minutes, seconds, period] = timeString.match(/^(\d{1,2}):(\d{2}):(\d{2}) (AM|PM)$/) || [];
  const adjustedHours = period === 'PM' && +hours < 12 ? +hours + 12 : period === 'AM' && +hours === 12 ? 0 : +hours;

  const date = new Date();
  date.setHours(adjustedHours, +minutes, +seconds, 0);

  return date;
}

function formatHourToDateTime(timeString: string): string {
  const [hours, minutes, seconds, period] = timeString.match(/(\d{2}):(\d{2}):(\d{2}) (AM|PM)/)!.slice(1);
  const adjustedHours = period === 'PM' && +hours < 12 ? +hours + 12 : period === 'AM' && +hours === 12 ? 0 : +hours;

  const date = new Date();
  date.setHours(adjustedHours, +minutes, +seconds, 0);

  return date.toString();
}

function calculatedYearlyBooster(boosterConfig: { booster: string; booster_unit: string }) {
  const { booster, booster_unit } = boosterConfig;
  const boosterValue = parseInt(booster, 10);

  const unitMapping: { [key: string]: 'days' | 'weeks' | 'months' | 'years' } = {
    days: 'days',
    weeks: 'weeks',
    months: 'months',
    years: 'years',
  };

  const unit = unitMapping[booster_unit];
  const now = new Date();
  const nextDate = new Date(now);

  if (unit) {
    switch (unit) {
      case 'days':
        nextDate.setDate(now.getDate() + boosterValue);
        break;
      case 'weeks':
        nextDate.setDate(now.getDate() + boosterValue * 7);
        break;
      case 'months':
        nextDate.setMonth(now.getMonth() + boosterValue);
        break;
      case 'years':
        nextDate.setFullYear(now.getFullYear() + boosterValue);
        break;
    }
  }
}

function formatDateToSimpleString(dateTimeString: string): string {
  if (!dateTimeString) return '-';

  const [datePart] = dateTimeString.split(' ');
  return datePart;
}

function dateStringToUtc(date: string) {
  return moment.tz(date, 'UTC').format('YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]');
}

function ageCalculator(date: Date, longDate: boolean = false) {
  const { t } = useI18n();

  const today = moment();
  const birthday = parseDate(date);

  const years = today.diff(birthday, 'years');
  birthday.add(years, 'years');

  const months = today.diff(birthday, 'months');
  birthday.add(months, 'months');

  const days = today.diff(birthday, 'days');

  if (longDate)
    return (
      (years !== 0 ? `${years} ${t('common.year', years)}, ` : '') +
      (months !== 0 ? `${months}  ${t('common.month', months)}, ` : '') +
      `${days}  ${t('common.day', days)}`
    );

  return `${years} y, ${months} m, ${days} d`;
}

function dateRangeLabel(startDate: any, endDate: any) {
  const { t } = useI18n();

  const currentDate = moment().startOf('day');
  const start = parseDate(startDate).startOf('day');
  const end = parseDate(endDate).startOf('day');

  const range = {
    last3Days:
      start.isSame(currentDate.clone().subtract(3, 'days')) && end.isSame(currentDate.clone().subtract(0, 'days')),
    last5Days:
      start.isSame(currentDate.clone().subtract(5, 'days')) && end.isSame(currentDate.clone().subtract(0, 'days')),
    last15Days:
      start.isSame(currentDate.clone().subtract(15, 'days')) && end.isSame(currentDate.clone().subtract(0, 'days')),
    last7Days:
      start.isSame(currentDate.clone().subtract(7, 'days')) && end.isSame(currentDate.clone().subtract(0, 'days')),
    last30Days:
      start.isSame(currentDate.clone().subtract(30, 'days')) && end.isSame(currentDate.clone().subtract(0, 'days')),
    today: start.isSame(currentDate) && end.isSame(currentDate),
    yesterday:
      start.isSame(currentDate.clone().subtract(1, 'days')) && end.isSame(currentDate.clone().subtract(1, 'days')),
    thisMonth:
      start.isSame(currentDate.clone().startOf('month'), 'day') &&
      end.isSame(currentDate.clone().endOf('month'), 'day'),
    lastMonth:
      start.isSame(currentDate.clone().subtract(1, 'month').startOf('month'), 'day') &&
      end.isSame(currentDate.clone().subtract(1, 'month').endOf('month'), 'day'),
    thisYear:
      start.isSame(currentDate.clone().startOf('year'), 'day') && end.isSame(currentDate.clone().endOf('year'), 'day'),
    lastYear:
      start.isSame(currentDate.clone().subtract(1, 'year').startOf('year'), 'day') &&
      end.isSame(currentDate.clone().subtract(1, 'year').endOf('year'), 'day'),
  };

  //prettier-ignore
  switch (true) {
    case range.today: return t('common.today');
    case range.yesterday: return t('common.yesterday');
    case range.last3Days: return t('common.last3Days');
    case range.last5Days: return t('common.last5Days');
    case range.last7Days: return t('common.last7Days');
    case range.last15Days: return t('common.last15Days');
    case range.thisMonth: return t('common.thisMonth');
    case range.last30Days: return t('common.last30Days');
    case range.lastMonth: return t('common.lastMonth');
    case range.thisYear: return t('common.thisYear');
    case range.lastYear: return t('common.lastYear');
    default: return `${getDefaultDateFormat(startDate)} - ${getDefaultDateFormat(endDate)}`;
  }
}

// NOTE :: A function, not a constant, because the label carries a live sample of the
// format ("DD/MM/YYYY - 01/09/2026"). A module-scope constant freezes that sample at the
// moment the bundle first evaluates, so a tab left open overnight would show yesterday.
function getDateFormatOptions() {
  return PHP_DATE_FORMATS.map((phpDateFormat) => {
    const momentFormat = PHP_TO_MOMENT_DATE_FORMAT[phpDateFormat];

    return {
      label: `${momentFormat} - ${moment().format(momentFormat)}`,
      value: phpDateFormat,
    };
  });
}

export {
  ageCalculator,
  calculatedYearlyBooster,
  dateRelation,
  dateStringToUtc,
  formatDateTime,
  formatDateToSimpleString,
  formatDateToString,
  formatHourToDateTime,
  formatNumberToDate,
  formatStringToRelation,
  formatTimestampToDate,
  formatTimestampToDateTime,
  formatTimestampToSimpleDate,
  getDateFormatOptions,
  getDateFromOId,
  getDefaultDateFormat,
  getDefaultFormat,
  getParsedDate,
  getPayloadDateString,
  getPayloadDateTimeString,
  getPayloadEventDateString,
  getWeightDateTimestamp,
  hasValidWeightDate,
  getTimeFromDate,
  handleInvalidTime,
  isAfDate,
  isValidDate,
  numberToDate,
  timestampToTime,
  toIsoString,
  getPayloadDate,
  dateRangeLabel,
  DEFAULT_TIME_FORMAT,
};
